import { useState } from "react";
import { Link } from "react-router";
import { fmtNum } from "../lib/formato";
import { mensagemDeErro, rpc } from "../lib/rpc";
import { Botao, MensagemErro, Selo, Tabela } from "./ui";

export interface FaltaPedido {
  sku: string | null; nome: string; precisa: number; livre_separacao: number; falta: number; motivo: string;
  onde_esta: Array<{ posicao: string; tipo: string; livre: number; trava?: string | null; liberavel?: boolean }>;
}

/**
 * O que falta no picking e no packing para separar o pedido e onde está o estoque.
 * Posição de separação travada (contagem pendente, bloqueio ou inativa): o líder libera ali mesmo.
 */
export function FaltasPedido({ faltas, lider, onAtualizar }: {
  faltas: FaltaPedido[]; lider: boolean; pedidoId?: string; numero?: number | null; onAtualizar?: () => void;
}) {
  const [ocupado, setOcupado] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  if (!faltas.length) return ok ? <p role="status" className="rounded-md bg-ok-claro px-3 py-2 font-semibold text-ok">{ok}</p> : null;

  async function liberar(posicao: string) {
    setOcupado(posicao);
    setErro(null);
    try {
      const r = await rpc<{ ok: boolean; mensagem?: string; contagens_mantidas?: number }>("wms_local_liberar_separacao", { p_codigo: posicao });
      if (!r.ok) return setErro(r.mensagem ?? "Não foi possível liberar a posição");
      setOk(`Posição ${posicao} liberada para a separação${r.contagens_mantidas ? " (a contagem continua na lista do estoquista)" : ""}. O pedido voltou para a fila.`);
      onAtualizar?.();
    } catch (e) {
      setErro(mensagemDeErro(e));
    } finally {
      setOcupado(null);
    }
  }

  return (
    <div className="flex flex-col gap-2 rounded-md border-2 border-erro bg-chapa p-3">
      <p className="font-semibold">Falta no picking e no packing para separar:</p>
      <Tabela cabecalho={["Produto", "Precisa", "Livre p/ separar", "Onde está", "O que fazer"]}>
        {faltas.map((f, n) => (
          <tr key={n}>
            <td>{f.sku} <span className="text-aco-escuro">{f.nome}</span></td>
            <td>{fmtNum(f.precisa)}</td>
            <td><Selo tom="erro">{fmtNum(f.livre_separacao)}</Selo></td>
            <td className="text-sm">
              {f.onde_esta.length ? f.onde_esta.slice(0, 4).map((o) => (
                <div key={o.posicao} className="mb-1">
                  <span className="font-semibold">{o.posicao}</span> <span className="text-aco-escuro">({o.tipo.toLowerCase()}, {fmtNum(o.livre)})</span>
                  {o.trava && <span className="block text-erro">{o.trava}</span>}
                  {o.liberavel && lider && (
                    <Botao variante="secundario" className="mt-1" disabled={!!ocupado} onClick={() => void liberar(o.posicao)}>
                      {ocupado === o.posicao ? "Liberando..." : "Liberar para a separação"}
                    </Botao>
                  )}
                </div>
              )) : <span className="text-aco-escuro">nenhuma posição</span>}
            </td>
            <td className="text-sm">{f.motivo}</td>
          </tr>
        ))}
      </Tabela>
      <MensagemErro>{erro}</MensagemErro>
      <div className="flex flex-wrap gap-2">
        <Link to="/estoque/transferir" className="inline-flex min-h-12 items-center rounded-md border-2 border-tinta px-4 font-semibold">Transferir para o picking</Link>
      </div>
      <p className="text-sm text-aco-escuro">Com o estoque livre no picking ou no packing o pedido volta sozinho para a fila.</p>
    </div>
  );
}
